/**
 * Shared helpers for the field kinds (drug class / generic / brand /
 * manufacturer / application number / UNII / set ID / DEA schedule): display labels and the literal-core
 * check used by the keyword builder and its suggestion popover. Drug class is
 * `pharm_class`; the retired `tag` kind stays only so an old chip renders
 * ("Legacy class").
 */
import type { ChipKind } from '../types';

export const KIND_LABELS: Record<ChipKind, string> = {
  pharm_class: 'Drug Class',
  tag: 'Legacy class',
  generic: 'Generic',
  brand: 'Brand',
  manufacturer: 'Manufacturer',
  application: 'Application No.',
  unii: 'UNII',
  set_id: 'Set ID',
  dea_schedule: 'DEA Schedule',
};

/** Kinds matched by exact value only (spec 2026-10-26): no Near/pattern. */
export const EXACT_ONLY_KINDS: ReadonlySet<ChipKind> = new Set<ChipKind>(['pharm_class', 'unii', 'set_id', 'dea_schedule']);

/** Placeholder for an exact-value row (other kinds: "<Kind> contains…"). */
export const EXACT_ONLY_PLACEHOLDER: Partial<Record<ChipKind, string>> = {
  pharm_class: 'Drug class — pick a suggestion…',
  unii: 'UNII code or substance…',
  set_id: 'Set ID (type 4+ characters)…',
  dea_schedule: 'DEA schedule (CI to CV)…',
};

/** True when a typed value has a literal core left after stripping every
 *  ``*`` and one trailing ``+`` — mirrors the server's rejection so the client
 *  can refuse before a request is made. */
export function hasLiteralCore(value: string): boolean {
  const withoutPlus = value.endsWith('+') ? value.slice(0, -1) : value;
  return withoutPlus.replace(/\*/g, '').trim().length > 0;
}
